/**
 * fb-bundle-base.js
 * [FROST BUDDY] Shared base class for bundle page custom elements.
 * Extended by FbBundlePage (fb-bundle-page.liquid) and FbBundleLeague (fb-bundle-league.liquid).
 * NOT registered as a custom element — subclasses register themselves.
 */

window.FbBundleBase = class FbBundleBase extends HTMLElement {

  /* ══════════════════════════════════════
     SETUP — called by subclass connectedCallback
  ══════════════════════════════════════ */

  _initConfig(cfg) {
    this._cfg = cfg;
    // cfg: { sectionId, parentVariantId, parentVariantTitle, locationTemplate, buttonText, sourceId }
  }

  _initSharedRefs() {
    this.selectedItems = [];
    this._expandOpen = false;

    this.addBtn = this.querySelector('[data-bundle-add-to-cart]');
    this.mobileAddBtn = this.querySelector('[data-dynamic-mobile-add]');
    this.slotsContainer = this.querySelector('[data-dynamic-slots]');
    this.statusCirclesContainer = this.querySelector('[data-dynamic-status-circles]');
    this.counterEl = this.querySelector('[data-dynamic-counter]');
    this.mobileCounterEl = this.querySelector('[data-dynamic-mobile-counter]');
    this.tierNudgeEl = this.querySelector('[data-dynamic-tier-nudge]');
    this.mobileTierNudgeEl = this.querySelector('[data-dynamic-mobile-tier-nudge]');
    this.dynPriceFinal = this.querySelector('[data-dynamic-price-final]');
    this.dynPriceCompare = this.querySelector('[data-dynamic-price-compare]');
    this.dynPriceSavings = this.querySelector('[data-dynamic-price-savings]');
    this.dynMobilePrice = this.querySelector('[data-dynamic-mobile-price]');
    this.dynMobileCompare = this.querySelector('[data-dynamic-mobile-compare]');
    this.expandToggle = this.querySelector('[data-expand-toggle]');
    this.expandPanel = this.querySelector('[data-expand-panel]');
  }

  _bindShared() {
    this._bindExpandToggle();
    this._bindBackToTop();
    this.bindSummaryRemove();
    this.bindStatusCircles();
    this.bindAddToCart();
  }

  /* ══════════════════════════════════════
     EXPAND TOGGLE (mobile)
  ══════════════════════════════════════ */

  _bindExpandToggle() {
    var self = this;
    if (this.expandToggle) {
      this.expandToggle.addEventListener('click', function () {
        self._expandOpen = !self._expandOpen;
        self.expandPanel.classList.toggle('fb-bundle-dynamic__expand-panel--open', self._expandOpen);
        self.expandToggle.classList.toggle('fb-bundle-dynamic__expand-toggle--open', self._expandOpen);
        if (self.statusCirclesContainer) {
          self.statusCirclesContainer.style.display = self._expandOpen ? 'none' : '';
        }
        if (self.mobileTierNudgeEl) {
          self.mobileTierNudgeEl.style.display = self._expandOpen ? 'none' : '';
        }
      });
    }
    if (this.expandPanel) {
      this.expandPanel.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('[data-slot-remove]');
        if (removeBtn && removeBtn.dataset.variantId) {
          self.removeByVariantId(removeBtn.dataset.variantId);
          return;
        }
        var slotEl = e.target.closest('[data-dynamic-slot]');
        if (slotEl && slotEl.dataset.variantId) self.scrollToCard(slotEl.dataset.variantId);
      });
    }
  }

  /* ══════════════════════════════════════
     BACK TO TOP
  ══════════════════════════════════════ */

  _bindBackToTop() {
    var self = this;
    this._backToTopBtns = this.querySelectorAll('[data-back-to-top]');
    if (!this._backToTopBtns.length) return;

    this._backToTopBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var picker = self.querySelector('.fb-bundle-dynamic__picker');
        if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    var picker = this.querySelector('.fb-bundle-dynamic__picker');
    if (picker && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        self._backToTopBtns.forEach(function (btn) {
          btn.classList.toggle('fb-bundle-dynamic__back-to-top--visible', !visible);
        });
      }, { threshold: 0 }).observe(picker);
    }
  }

  /* ══════════════════════════════════════
     CARD INTERACTIONS
  ══════════════════════════════════════ */

  bindCards() {
    var self = this;
    this.querySelectorAll('.fb-bundle-card').forEach(function (card) {
      card.addEventListener('click', function () {
        if (card.disabled) return;
        self.toggleCard(card);
      });
    });
  }

  // Default dynamic toggle — subclass overrides for fixed mode
  toggleCard(cardEl) {
    var variantId = cardEl.dataset.variantId;
    var existingIndex = -1;
    for (var i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].variantId === variantId) { existingIndex = i; break; }
    }

    if (existingIndex >= 0) {
      this.selectedItems.splice(existingIndex, 1);
      this._updateCardSelection(variantId, false);
      this.render();
      this._onSelectionChange();
    } else {
      if (this.maxItems > 0 && this.selectedItems.length >= this.maxItems) return;
      var data = this._extractCardData(cardEl);
      this.selectedItems.push(data);
      this._updateCardSelection(variantId, true);
      this.flyToSummary(cardEl);
      this.render();
      this.flashLastItem();
      this.flashCounter(data.title);
      this._onSelectionChange();
    }
  }

  _extractCardData(cardEl) {
    return {
      variantId: cardEl.dataset.variantId,
      price: Number(cardEl.dataset.price),
      compareAtPrice: Number(cardEl.dataset.compareAtPrice || cardEl.dataset.price),
      title: cardEl.dataset.productTitle || '',
      image: cardEl.dataset.productImage || '',
      designName: cardEl.dataset.designName || ''
    };
  }

  _updateCardSelection(variantId, selected) {
    this.querySelectorAll('.fb-bundle-card[data-variant-id="' + variantId + '"]').forEach(function (c) {
      c.classList.toggle('fb-bundle-card--selected', selected);
    });
  }

  removeByVariantId(variantId) {
    var idx = -1;
    for (var i = 0; i < this.selectedItems.length; i++) {
      if (this.selectedItems[i].variantId === variantId) { idx = i; break; }
    }
    if (idx < 0) return;
    this.selectedItems.splice(idx, 1);
    this._updateCardSelection(variantId, false);
    this.render();
    this._onSelectionChange();
  }

  // Hook for subclasses (e.g. save state)
  _onSelectionChange() {}

  bindSummaryRemove() {
    var self = this;
    this.slotsContainer.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-slot-remove]');
      if (removeBtn) { self.removeByVariantId(removeBtn.dataset.variantId); return; }
      var slotEl = e.target.closest('[data-dynamic-slot]');
      if (slotEl && slotEl.dataset.variantId) self.scrollToCard(slotEl.dataset.variantId);
    });
  }

  bindStatusCircles() {
    var self = this;
    this.statusCirclesContainer.addEventListener('click', function (e) {
      var circle = e.target.closest('[data-status-circle]');
      if (circle && circle.dataset.variantId) self.scrollToCard(circle.dataset.variantId);
    });
  }

  bindAddToCart() {
    var self = this;
    if (this.addBtn) this.addBtn.addEventListener('click', function () { self.handleAddToCart(); });
    if (this.mobileAddBtn) this.mobileAddBtn.addEventListener('click', function () { self.handleAddToCart(); });
  }

  scrollToCard(variantId) {
    var card = this.querySelector('.fb-bundle-card[data-variant-id="' + variantId + '"]:not(.fb-bundle-card--tab-hidden)')
      || this.querySelector('.fb-bundle-card[data-variant-id="' + variantId + '"]');
    if (!card) return;

    // Switch to card's tab if hidden
    if (card.classList.contains('fb-bundle-card--tab-hidden') && card.dataset.blockId) {
      var tab = this.querySelector('[data-dynamic-tab="' + card.dataset.blockId + '"]');
      if (tab) tab.click();
    }

    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.style.transition = 'box-shadow 0.2s ease';
    card.style.boxShadow = '0 0 0 3px rgba(50,100,158,0.5)';
    setTimeout(function () { card.style.boxShadow = ''; }, 1200);
  }

  /* ══════════════════════════════════════
     FLY ANIMATION
  ══════════════════════════════════════ */

  flyToSummary(cardEl) {
    var imgUrl = cardEl.dataset.productImage;
    if (!imgUrl) return;
    var isMobile = window.innerWidth < 750;
    var sourceRect = cardEl.getBoundingClientRect();
    var destEl = isMobile ? this.statusCirclesContainer : this.slotsContainer;
    if (!destEl) return;
    var destRect = destEl.getBoundingClientRect();

    var flyEl = document.createElement('div');
    flyEl.className = 'fb-bundle-dynamic__fly' + (isMobile ? ' fb-bundle-dynamic__fly--mobile' : '');
    flyEl.style.setProperty('--start-x', (sourceRect.left + sourceRect.width / 2) + 'px');
    flyEl.style.setProperty('--start-y', (sourceRect.top + sourceRect.height / 2) + 'px');
    flyEl.style.setProperty('--travel-x', (destRect.left + destRect.width / 2 - sourceRect.left - sourceRect.width / 2) + 'px');
    flyEl.style.setProperty('--travel-y', (destRect.top + 20 - sourceRect.top - sourceRect.height / 2) + 'px');
    flyEl.style.backgroundImage = 'url(' + imgUrl + ')';

    document.body.appendChild(flyEl);
    flyEl.addEventListener('animationend', function () { flyEl.remove(); });
    setTimeout(function () { if (flyEl.parentNode) flyEl.remove(); }, 800);
  }

  /* ══════════════════════════════════════
     FEEDBACK
  ══════════════════════════════════════ */

  flashLastItem() {
    var slots = this.slotsContainer.querySelectorAll('[data-dynamic-slot]');
    var last = slots[slots.length - 1];
    if (!last) return;
    last.classList.add('fb-bundle-dynamic__slot--just-filled');
    setTimeout(function () { last.classList.remove('fb-bundle-dynamic__slot--just-filled'); }, 800);
  }

  flashCounter(productTitle) {
    var self = this;
    var msg = '\u2713 ' + productTitle + ' added';
    if (this._counterFlashTimer) clearTimeout(this._counterFlashTimer);

    [this.counterEl, this.mobileCounterEl].forEach(function (el) {
      if (el) { el.textContent = msg; el.classList.add('fb-bundle-dynamic__counter--confirmed'); }
    });
    [this.tierNudgeEl, this.mobileTierNudgeEl].forEach(function (el) {
      if (el) el.setAttribute('hidden', '');
    });

    this._counterFlashTimer = setTimeout(function () {
      [self.counterEl, self.mobileCounterEl].forEach(function (el) {
        if (el) el.classList.remove('fb-bundle-dynamic__counter--confirmed');
      });
      self.renderCounter();
      self.renderTierNudge();
    }, 3000);
  }

  /* ══════════════════════════════════════
     RENDERING
  ══════════════════════════════════════ */

  render() {
    this.renderSummaryItems();
    this.renderStatusCircles();
    this.renderCounter();
    this.renderTierNudge();
    this.updatePrice();
    this.updateCardPrices();
  }

  renderSummaryItems() {
    var self = this;
    var html = '';

    if (this.selectedItems.length === 0) {
      html = '<p class="fb-bundle-dynamic__empty-msg">Select products to build your bundle</p>';
    } else {
      this.selectedItems.forEach(function (item) {
        html += '<div class="fb-bundle-dynamic__slot fb-bundle-dynamic__slot--entering" data-dynamic-slot data-variant-id="' + item.variantId + '">';
        html += '<div class="fb-bundle-dynamic__slot-thumb"><img src="' + self.escapeHtml(item.image) + '" alt="' + self.escapeHtml(item.title) + '" loading="lazy"></div>';
        html += '<span class="fb-bundle-dynamic__slot-name">' + self.escapeHtml(item.title) + '</span>';
        html += '<span class="fb-bundle-dynamic__slot-price">' + self.formatMoney(item.price) + '</span>';
        html += '<button type="button" class="fb-bundle-dynamic__slot-remove" data-slot-remove data-variant-id="' + item.variantId + '" aria-label="Remove">&times;</button>';
        html += '</div>';
      });

      // Empty slot hints with tier labels
      var count = this.selectedItems.length;
      if (this.maxItems > 0 && count < this.maxItems) {
        for (var i = count; i < this.maxItems; i++) {
          var needed = i + 1 - count;
          var slotTier = this.getTierForCount(i + 1);
          var prevTier = this.getTierForCount(count);
          var tierLabel = '';
          if (slotTier && slotTier.discount > (prevTier ? prevTier.discount : 0)) {
            tierLabel = 'Add ' + needed + ' more for ' + slotTier.discount + '% off bundle';
          }
          html += '<div class="fb-bundle-dynamic__slot fb-bundle-dynamic__slot--empty-hint">';
          html += '<div class="fb-bundle-dynamic__slot-thumb fb-bundle-dynamic__slot-thumb--empty"></div>';
          html += '<span class="fb-bundle-dynamic__slot-name fb-bundle-dynamic__slot-name--hint">' + (tierLabel || 'Add another item') + '</span>';
          html += '</div>';
        }
      }
    }

    this.slotsContainer.innerHTML = html;

    // Mirror into mobile expand panel
    if (this.expandPanel) this.expandPanel.innerHTML = html;
    if (this.expandToggle) {
      if (this.selectedItems.length > 0) {
        this.expandToggle.removeAttribute('hidden');
      } else {
        this.expandToggle.setAttribute('hidden', '');
        if (this.expandPanel) this.expandPanel.classList.remove('fb-bundle-dynamic__expand-panel--open');
        this._expandOpen = false;
        if (this.statusCirclesContainer) this.statusCirclesContainer.style.display = '';
      }
    }
  }

  renderStatusCircles() {
    var self = this;
    var html = '';
    var count = this.selectedItems.length;

    this.selectedItems.forEach(function (item) {
      html += '<div class="fb-bundle-dynamic__status-slot">';
      html += '<div class="fb-bundle-dynamic__status-circle" data-status-circle data-variant-id="' + item.variantId + '">';
      html += '<img src="' + self.escapeHtml(item.image) + '" alt="' + self.escapeHtml(item.title) + '">';
      html += '</div></div>';
    });

    // Empty circles with per-slot tier labels
    if (this.maxItems > 0 && count < this.maxItems) {
      var baseTier = this.getTierForCount(count);
      var baseDiscount = baseTier ? baseTier.discount : 0;
      for (var i = count; i < this.maxItems; i++) {
        var slotTier = this.getTierForCount(i + 1);
        var label = (slotTier && slotTier.discount > baseDiscount) ? slotTier.discount + '% off bundle' : '';
        html += '<div class="fb-bundle-dynamic__status-slot">';
        html += '<div class="fb-bundle-dynamic__status-circle fb-bundle-dynamic__status-circle--empty"></div>';
        if (label) html += '<span class="fb-bundle-dynamic__status-slot-label">' + label + '</span>';
        html += '</div>';
      }
    }

    this.statusCirclesContainer.innerHTML = html;
  }

  renderCounter() {
    var n = this.selectedItems.length;
    var text, isFull = false;

    if (this.maxItems > 0 && n >= this.maxItems) {
      text = 'Bundle full \u2014 ' + this.maxItems + ' items max';
      isFull = true;
    } else if (n < this.minItems) {
      var r = this.minItems - n;
      text = 'Add ' + r + ' more item' + (r !== 1 ? 's' : '') + ' to unlock discount';
    } else {
      text = n + ' item' + (n !== 1 ? 's' : '') + ' in your bundle';
    }

    [this.counterEl, this.mobileCounterEl].forEach(function (el) {
      if (!el) return;
      el.textContent = text;
      el.classList.toggle('fb-bundle-dynamic__counter--full', isFull);
    });
  }

  renderTierNudge() {
    var showNudge = false;

    if (this.discountTiers.length >= 2) {
      var n = this.selectedItems.length;
      var currentTier = this.getTierForCount(n);
      var nextTier = null;
      for (var i = 0; i < this.discountTiers.length; i++) {
        if (this.discountTiers[i].count > n) { nextTier = this.discountTiers[i]; break; }
      }
      if (n >= this.minItems && nextTier && nextTier.discount > (currentTier ? currentTier.discount : 0) && (this.maxItems === 0 || nextTier.count <= this.maxItems)) {
        var needed = nextTier.count - n;
        var text = 'Add ' + needed + ' more for ' + nextTier.discount + '% off!';
        [this.tierNudgeEl, this.mobileTierNudgeEl].forEach(function (el) {
          if (el) { el.textContent = text; el.removeAttribute('hidden'); }
        });
        showNudge = true;
      }
    }

    if (!showNudge) {
      [this.tierNudgeEl, this.mobileTierNudgeEl].forEach(function (el) {
        if (el) el.setAttribute('hidden', '');
      });
    }
  }

  getTierForCount(count) {
    var tier = null;
    for (var i = 0; i < this.discountTiers.length; i++) {
      if (count >= this.discountTiers[i].count) tier = this.discountTiers[i];
    }
    return tier;
  }

  updatePrice() {
    var total = 0;
    var n = this.selectedItems.length;
    this.selectedItems.forEach(function (item) { total += item.price; });

    var tier = this.discountTiers.length > 0 ? this.getTierForCount(n) : null;
    var discount = tier ? tier.discount : this.discountPercent;
    var finalPrice = Math.round(total * (1 - discount / 100));
    var savings = total - finalPrice;
    var minMet = n >= this.minItems;

    if (this.dynPriceFinal) this.dynPriceFinal.textContent = n > 0 ? this.formatMoney(finalPrice) : '';
    if (this.dynPriceCompare) this.dynPriceCompare.textContent = n > 0 ? this.formatMoney(total) : '';
    if (this.dynPriceSavings) this.dynPriceSavings.textContent = n > 0 ? 'Save ' + this.formatMoney(savings) : '';
    if (this.dynMobilePrice) this.dynMobilePrice.textContent = n > 0 ? this.formatMoney(finalPrice) : '';
    if (this.dynMobileCompare) this.dynMobileCompare.textContent = n > 0 ? this.formatMoney(total) : '';

    var remaining = this.minItems - n;
    var notMetText = 'Add ' + remaining + ' more item' + (remaining !== 1 ? 's' : '') + ' to unlock discount';
    var buttonText = this._cfg.buttonText;
    [this.addBtn, this.mobileAddBtn].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = !minMet;
      btn.textContent = minMet ? buttonText : notMetText;
    });
  }

  updateCardPrices() {
    var self = this;
    var n = this.selectedItems.length;
    // Always preview the NEXT tier's discount (or first tier if none unlocked yet)
    var discount = 0;
    if (this.discountTiers.length > 0) {
      var nextTier = null;
      for (var i = 0; i < this.discountTiers.length; i++) {
        if (this.discountTiers[i].count > n) { nextTier = this.discountTiers[i]; break; }
      }
      if (nextTier) {
        discount = nextTier.discount;
      } else {
        // Already at highest tier — show current tier's discount
        var currentTier = this.getTierForCount(n);
        discount = currentTier ? currentTier.discount : this.discountPercent || 0;
      }
    } else {
      discount = this.discountPercent || 0;
    }

    this.querySelectorAll('.fb-bundle-card').forEach(function (card) {
      var priceEl = card.querySelector('.fb-bundle-card__price');
      if (!priceEl) return;
      var originalCents = Number(card.dataset.price);
      if (!originalCents || !discount) {
        priceEl.innerHTML = self.formatMoney(originalCents);
        return;
      }
      var discountedCents = Math.round(originalCents * (1 - discount / 100));
      priceEl.innerHTML = '<s class="fb-bundle-card__price-original">' + self.formatMoney(originalCents) + '</s> ' + self.formatMoney(discountedCents);
    });
  }

  /* ══════════════════════════════════════
     ADD TO CART
  ══════════════════════════════════════ */

  handleAddToCart() {
    var self = this;
    var n = this.selectedItems.length;
    if (n < this.minItems) return;

    var bundleId = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    var tier = this.discountTiers.length > 0 ? this.getTierForCount(n) : null;
    var parentVariant = tier ? tier.variantId : this._cfg.parentVariantId;
    var discount = tier ? tier.discount : this.discountPercent;

    var items = this.selectedItems.map(function (item) {
      return {
        id: Number(item.variantId), quantity: 1,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_parent_variant': String(parentVariant),
          '_bundle_discount': String(discount),
          '_bundle_title': (tier && tier.title) ? tier.title : self._cfg.parentVariantTitle,
          '_location_template': self._cfg.locationTemplate,
        },
      };
    });

    [this.addBtn, this.mobileAddBtn].forEach(function (btn) {
      if (btn) { btn.classList.add('fb-bundle__add-btn--loading'); btn.disabled = true; }
    });

    var cartAddUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    var sourceId = this._cfg.sourceId + '-' + this._cfg.sectionId;

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status) { self.showError(data.description || data.message || 'Could not add to cart'); return; }
        self._onAddToCartSuccess(data, items);

        // [FROST BUDDY] v4.0.0 cart components (cart-icon, cart-drawer, etc.) listen
        // for the @shopify/events CartLinesUpdateEvent — event name is
        // "shopify:cart:lines-update" (HYPHEN between "lines" and "update", NOT a colon
        // — confirmed against the live StandardEvents module). The .d.ts typing file
        // even has the wrong format, so don't trust it. Properties (`action`, `context`,
        // `lines`, `promise`) are set directly on the event instance via Object.assign
        // in the upstream ShopifyStandardEvent constructor; mirroring that via
        // CustomEvent + manual property assignment is functionally equivalent and lets
        // fb-bundle-base.js stay a non-module classic script. Resolve the promise with
        // the fresh cart so cart-icon's bubble update path (cart?.totalQuantity) works.
        var ev = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
        ev.action = 'add';
        ev.context = 'product';
        ev.lines = items.map(function (item) {
          return { merchandiseId: String(item.id), quantity: item.quantity };
        });
        ev.promise = fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            return {
              cart: {
                id: cart.token || '',
                totalQuantity: cart.item_count || 0,
                cost: {
                  totalAmount: {
                    amount: ((cart.total_price || 0) / 100).toFixed(2),
                    currencyCode: cart.currency || 'USD',
                  },
                },
                lines: (cart.items || []).map(function (it) {
                  return {
                    id: String(it.key || it.id || ''),
                    quantity: it.quantity || 0,
                    cost: {
                      totalAmount: {
                        amount: ((it.final_line_price || 0) / 100).toFixed(2),
                        currencyCode: cart.currency || 'USD',
                      },
                    },
                  };
                }),
                discountCodes: (cart.cart_level_discount_applications || []).map(function (d) {
                  return { applicable: true, code: d.title || '' };
                }),
              },
              detail: {
                sourceId: sourceId,
                source: self._cfg.sourceId,
                itemCount: cart.item_count || 0,
              },
            };
          })
          .catch(function () {
            return { cart: null, detail: { sourceId: sourceId, source: self._cfg.sourceId, itemCount: (data.items ? data.items.length : items.length) } };
          });

        document.dispatchEvent(ev);
      })
      .catch(function () { self.showError('Something went wrong. Please try again.'); })
      .finally(function () {
        [self.addBtn, self.mobileAddBtn].forEach(function (btn) {
          if (btn) { btn.classList.remove('fb-bundle__add-btn--loading'); btn.disabled = false; }
        });
        self.updatePrice();
      });
  }

  // Hook for subclasses (e.g. league clears state + selections)
  _onAddToCartSuccess(data, items) {}

  showError(msg) {
    var self = this;
    [this.addBtn, this.mobileAddBtn].forEach(function (btn) {
      if (btn) { btn.textContent = msg; btn.style.backgroundColor = '#dc2626'; }
    });
    setTimeout(function () {
      [self.addBtn, self.mobileAddBtn].forEach(function (btn) {
        if (btn) btn.style.backgroundColor = '';
      });
      self.updatePrice();
    }, 2500);
  }

  /* ══════════════════════════════════════
     UTILITIES
  ══════════════════════════════════════ */

  formatMoney(cents) { return '$' + (cents / 100).toFixed(2); }
  escapeHtml(str) { var div = document.createElement('div'); div.textContent = str; return div.innerHTML; }

  _parseTiers(sectionId) {
    this.discountTiers = [];
    var tiersScript = document.querySelector('[data-bundle-tiers-' + sectionId + ']');
    if (tiersScript) {
      try { this.discountTiers = JSON.parse(tiersScript.textContent); } catch (e) {}
      this.discountTiers.sort(function (a, b) { return a.count - b.count; });
    }
  }
};
